import {
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
  Stack,
  Table,
  TableCell,
  TableRow,
  Typography
} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TableBody from '@mui/material/TableBody';
import { useMutation, useQuery } from "@tanstack/react-query";
import MUIDataTable, { MUIDataTableOptions } from "mui-datatables";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoGear } from "react-icons/go";
import { IoMdCheckboxOutline } from "react-icons/io";
import { MdOutlineLocalPostOffice } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../../context/UserContext";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Notifications from "../../service/Notifications";
import queryClient from "../../service/query";
import Reports from "../../service/Reports";
import Users from "../../service/Users";
import { NotificationType } from "../../types/notifications/types";
import { tableOptions } from "../TableHelpers/options";

const notificationsService = new Notifications();
const reportsService = new Reports();
const usersService = new Users();

const defaultColumns = {
  timestamp: true,
  nomeNotif: true,
  localNome: true,
  conjuntoNome: true,
  unidadeNome: true,
  nomeForm: true,
  lidoEm: true
};

export default function AllNotifications() {
  const { userData, saveUserData } = useContext(UserContext);
  const { t } = useTranslation('translation');
  const [open, setOpen] = useState(false);
  const [columnsViews, setColmnsViews] = useState(() => {
    try {
      const localColumnsViews = localStorage.getItem('columnsNotif');
      return localColumnsViews ? JSON.parse(localColumnsViews) : defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const [selected, setSelected] = useState(false);
  const [hour, setHour] = useState<string>('1');
  const [when, setWhen] = useState<string>(userData.notificacao || 'sempre');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [rowsSelected, setRowsSelected] = useState<number[]>([]);
  const [modelosSelecionados, setModelosSelecionados] = useState<string[]>([]);

  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    trackEventMatomo('notificações', 'click', 'botão', open ? 'fecha configurações de recebimento' : 'abre configurações de recebimento');
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const { data, isLoading, isFetching, isSuccess, isError } = useQuery({
    queryKey: ['GET_NOTIFICATIONS'],
    queryFn: async () => {
      toast.info('Buscando notificações...', { toastId: 'fetchingNotifications' });;
      const token = await AttToken();
      if (token) {
        const response: NotificationType[] = await notificationsService.getByUser(token);
        return response;
      }
    },
    retry: 2,
    refetchInterval: 5 * 60 * 1000, // 5 minutes,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  const { mutate: update, isPending: loadingUpdate } = useMutation({
    mutationKey: ['UPDATE_NOTIFICATIONS'],
    mutationFn: async (value: string) => {
      const token = await AttToken();
      toast.info('Atualizando configuração...', { toastId: 'updatingNotifications' });
      if (!token) throw new Error('Token not found');
      const response = await usersService.changeNotificacao(token, value);
      if (typeof response === 'number') throw new Error('Notification settings were not updated');
      return response;
    },
    onSuccess: (_response, value) => {
      toast.success('Configurações atualizadas', { toastId: 'updatingNotifications' });
      saveUserData({
        ...userData,
        notificacao: value === 'diario' ? hour : value,
      });
      queryClient.resetQueries({ queryKey: ['GET_NOTIFICATIONS'] });
    },
    onError: () => {
      toast.error('Erro ao atualizar configurações', { toastId: 'updatingNotifications' });
    }
  });

  const { mutate: read, isPending: loadingRead } = useMutation({
    mutationKey: ['READ_NOTIFICATION'],
    mutationFn: async () => {
      const token = await AttToken();
      if (token) {
        for (const value of modelosSelecionados) {
          const body = { ...token, notif: value, date: Date.now(), user: token.username };
          await notificationsService.updateRead(body);
        }
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['GET_NOTIFICATIONS'] });
      setModelosSelecionados([]);
      setRowsSelected([]);
    },
    onError: () => {
      toast.error('Erro ao marcar notificação como lida', { toastId: 'markingAsRead' });
    }
  });

  const { mutate: openLink, isPending: loadingOpenLink } = useMutation({
    mutationKey: ['OPEN_LINK'],
    mutationFn: async (value: string) => {
      toast.warn(t('notifications.unread.openExe') as string);
      const token = await AttToken();
      if (token) {
        const form = data?.find((item: NotificationType) => item.id === value);
        if (form) {
          if (form.chamadoId) {
            navigate(`/detalheChamado/${form.chamadoId.split("_")[0]}`);
          } else {
            const response = await reportsService.getLink(token, form.userOrigem, null, Number(form.timestampForm), String(process.env.REACT_APP_PROJECT_NAME));
            return response;
          }
        }
      }
    },
    onSuccess: (data) => {
      if (data?.message) {
        window.open(data.message, '_blank', 'noopener,noreferrer');
      }
    },
    onError: () => {
      toast.error('Erro ao abrir link', { toastId: 'openingLink' });
    }
  });

  useEffect(() => {
    if (!userData.notificacao) return;
    if (userData.notificacao.length <= 2) {
      setWhen('diario');
      setHour(userData.notificacao);
    } else {
      setWhen(userData.notificacao);
    }
  }, [userData.notificacao]);

  const loading = isLoading || isFetching || loadingUpdate || loadingRead || loadingOpenLink;

  useEffect(() => {
    if (isSuccess && data?.length) {
      toast.success('Mensagens atualizadas', { toastId: 'fetchingNotifications' });
    } if (isError && !data?.length) {
      toast.error('Erro ao buscar notificações', { toastId: 'fetchingNotifications' });
    }
  }, [data, selected, loading, isSuccess, isError]);

  useEffect(() => {
    localStorage.setItem('columnsNotif', JSON.stringify(columnsViews));
  }, [columnsViews]);

  const getFilteredData = useCallback(() => {
    if (!data) return [];

    return data.filter((notification) => {
      const isRead = notification.lidoEm !== null &&
        notification.lidoEm !== undefined &&
        notification.lidoEm !== 0;

      return selected ? isRead : !isRead;
    });
  }, [selected, data]);

  useEffect(() => {
    setModelosSelecionados(
      rowsSelected
        .map((row) => getFilteredData()[row]?.id)
        .filter((id): id is string => Boolean(id))
    );
  }, [rowsSelected, getFilteredData]);

  const columns = [
    {
      name: "timestamp",
      label: t('notifications.unread.hour'),
      options: {
        display: columnsViews ? columnsViews.timestamp : true,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = new Date(value).toLocaleString("pt-br");
          return data;
        },
      }
    },
    {
      name: 'nomeNotif',
      label: t('notifications.unread.notificationName'),
      options: {
        customBodyRender: (value: string) => {
          return <Typography fontSize='small'>{value.replace('Notificação:', '')}</Typography>;
        },
        display: columnsViews ? columnsViews.nomeNotif : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'mensagem',
      label: t('message'),
      options: {
        viewColumns: false,
        display: false,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'localNome',
      label: t('settingsPage.hierarchy.place'),
      options: {
        display: columnsViews ? columnsViews.localNome : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: 'conjuntoNome',
      label: t('settingsPage.hierarchy.set'),
      options: {
        display: columnsViews ? columnsViews.conjuntoNome : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "unidadeNome",
      label: t('settingsPage.hierarchy.unity'),
      options: {
        display: columnsViews ? columnsViews.unidadeNome : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "nomeForm",
      label: t('notifications.unread.form'),
      options: {
        display: columnsViews ? columnsViews.nomeForm : true,
        filter: true,
        sort: true,
        sortThirdClickReset: true
      }
    },
    {
      name: "lidoEm",
      label: t('notifications.all.readAt'),
      options: {
        viewColumns: selected ? true : false,
        display: selected ? (columnsViews ? columnsViews.lidoEm : false) : false,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          const data = value ? new Date(value).toLocaleString("pt-br") : "";
          return data;
        },
      }
    },
    // {
    //   name: "id",
    //   label: t(`notifications.all.markAs`),
    //   options: {
    //     display: true,
    //     filter: false,
    //     sort: false,
    //     sortThirdClickReset: false,
    //     customBodyRender: (value: string) => (
    //       <Button
    //         endIcon={<MdOutlineLocalPostOffice />}
    //         style={{ display: selected ? 'none' : '' }}
    //         className="button is-light"
    //         onClick={(e) => {
    //           trackEventMatomo('notificações', 'click', 'button', 'abre notificação');
    //           e.preventDefault();
    //           read(value);
    //           toast.success(t(`notifications.unread.markedAsRead`) as string);
    //         }}
    //       >
    //         {t(`notifications.all.markAs`)}
    //       </Button>
    //     ),
    //   }
    // },
    {
      name: "id",
      label: t(`notifications.unread.openForm`),
      options: {
        display: true,
        filter: false,
        sort: false,
        sortThirdClickReset: false,
        customBodyRender: (value: string) => (
          <Button
            endIcon={<IoMdCheckboxOutline />}
            variant='contained'
            size="small"
            sx={{ textTransform: 'none' }}
            color="info"
            onClick={(e) => {
              e.preventDefault();
              trackEventMatomo('notificações', 'click', 'button', 'abre link');
              openLink(value);
            }}
          >
            {t(`notifications.unread.openForm`)}
          </Button>
        ),
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    rowsSelected: rowsSelected as [],
    viewColumns: true,
    selectableRowsHideCheckboxes: selected ?? false,
    expandableRows: true,
    expandableRowsHeader: false,
    expandableRowsOnClick: false,
    selectableRowsHeader: false,
    isRowExpandable: () => {
      return true;
    },
    renderExpandableRow: (_rowData) => {
      if (!_rowData[2]) return (

        <TableRow>
          <TableCell colSpan={_rowData.length}>
            <Typography variant="body2" color="textSecondary" align="center">
              Não há mensagem adicionada
            </Typography>
          </TableCell>
        </TableRow>
      );
      return (
        <TableRow sx={{ padding: 2, margin: 2 }}>
          <TableCell colSpan={6}>
            <Paper sx={{ width: '100%', padding: 2, margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t('message')}
              </Typography>
              <Table size="small" >
                <TableBody>
                  <TableRow>
                    <TableCell>{_rowData[2]}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </TableCell>
        </TableRow>
      );
    },
    onRowSelectionChange(_currentRowsSelected, allRowsSelected) {
      setRowsSelected(allRowsSelected.map((row) => row.dataIndex));
    },
    customToolbarSelect: () => {
      return <Button
        sx={{ marginRight: 2 }}
        disabled={!rowsSelected.length}
        endIcon={<MdOutlineLocalPostOffice />}
        onClick={(e) => {
          trackEventMatomo('notificações', 'click', 'button', 'abre notificação');
          e.preventDefault();
          read();
          toast.success(t(`notifications.unread.markedAsRead`) as string);
        }}
      >
        {t(`notifications.all.markAs`)}
      </Button>;
    },
    onViewColumnsChange: (changedColumn: string, action: string) => {
      if (action === 'remove') {
        setColmnsViews({
          ...columnsViews,
          [changedColumn]: false
        });
        localStorage.setItem('columnsNotif', JSON.stringify({
          ...columnsViews,
          [changedColumn]: false
        }));
      } else {
        setColmnsViews({
          ...columnsViews,
          [changedColumn]: true
        });
        localStorage.setItem('columnsNotif', JSON.stringify({
          ...columnsViews,
          [changedColumn]: false
        }));
      }
    },
    textLabels: {
      selectedRows: {
        text: `${t('message')}(s) selecionada(s)`,
        delete: `Abrir`,
      },
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`,
        viewColumns: `${t('notifications.unread.showColumns')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`,
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`,
      }
    }
  };

  const timeToSet = [];

  for (let i = 1; i <= 24; i++) {
    timeToSet.push(i);
  }

  if (!loading && !data?.length) {
    if (selected) {
      return <Typography textAlign='center' color='textDisabled' fontWeight={600} variant="h5">Você não tem notificações lidas no momento</Typography>;
    } else return <Typography textAlign='center' color='textDisabled' fontWeight={600} variant="h5">{t('notifications.all.noUnreadNotifications')}</Typography>;
  }

  const getMuiTheme = () =>
    createTheme({
      components: {
        MuiPaper: {
          styleOverrides: {
            elevation2: {
              width: 'fit-content',
            },
            elevation8: {
              width: 'fit-content',
            },
            root: {
              width: "100%",
            },
          }
        },
        MuiTableHead: {
          styleOverrides: {
            root: {
              span: {
                justifyContent: "flex-start !important",
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            input: {
              width: "fit-content",
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiList: {
          styleOverrides: {
            root: {
              display: "flex",
              flexDirection: "column",
            },
          },
        },
        MuiTableBody: {
          styleOverrides: {
            root: {
              td: {
                height: "fit-content",
                margin: 0,
                padding: "3px 10px",
                width: "fit-content",
              },
            },
          },
        },
        MuiGrid: {
          styleOverrides: {
            root: {
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiFormControl: {
          styleOverrides: {
            root: {
              padding: "10px",
            },
          },
        },
      },
    });

  return (
    <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={2}>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems='center'
        spacing={2}
      >
        <Button
          disabled={loading}
          color='success'
          variant='contained'
          endIcon={<GoGear />}
          onClick={handleClick}
        >
          {t('settings')}
        </Button>
        <Popper open={open} anchorEl={anchorEl} placement='bottom-start'>
          <Card sx={{ padding: 2 }}>
            <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={2}>
              <FormControl>
                <FormLabel>{t('notifications.all.recieveContent')}</FormLabel>
                <RadioGroup defaultValue={when}>
                  <FormControlLabel value='sempre'
                    control={<Radio checked={when === 'sempre'} onChange={() => {
                      trackEventMatomo('notificações', 'click', 'radio', 'recebe sempre');
                      setWhen('sempre');
                      update('sempre');
                    }} />} label={t('notifications.all.always')} />
                  <FormControlLabel value={when}
                    control={<Radio checked={when === 'nunca'}
                      onChange={() => {
                        trackEventMatomo('notificações', 'click', 'radio', 'nunca recebe');
                        setWhen('nunca');
                        update('nunca');
                      }}
                    />} label={t('notifications.all.never')} />
                  <FormControlLabel value='diario'
                    control={<Radio checked={when === 'diario'}
                      onChange={() => {
                        trackEventMatomo('notificações', 'click', 'radio', 'recebe diariamente');
                        setWhen('diario');
                        update('diario');
                      }}
                    />} label={t('notifications.all.daily')} />
                </RadioGroup>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">{t('notifications.selectWhen')}</InputLabel>
                <Select
                  disabled={when !== 'diario'}
                  value={hour}
                  label={t('notifications.selectWhen')}
                  onChange={(e) => {
                    trackEventMatomo('notificações', 'click', 'select', 'seleciona horário de recebimento');
                    setHour(e.target.value);
                    update(e.target.value);
                  }}
                >
                  {timeToSet.map((e: number, index) => (
                    <MenuItem key={index} value={`${e}`}>{`${e} : 00`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Card>
        </Popper>
        <Select
          disabled={loading}
          size="small"
          label={t(`notifications.all.selectStatus`)}
          defaultValue={'false'}
          onClick={() => trackEventMatomo('notificações', 'click', 'select', 'seleciona valor')}
          onChange={(eve) => {
            setSelected(eve.target.value === 'true');
            queryClient.resetQueries({ queryKey: ['GET_NOTIFICATIONS'] });
          }}
        >
          <MenuItem value={'false'}>{t(`notifications.all.notRead`)}</MenuItem>
          <MenuItem value={'true'}>{t(`notifications.all.read`)}</MenuItem>
        </Select>
      </Stack>
      {loading
        ? <Skeleton variant="rounded" height={600} width={'100%'} />
        : <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            key={String(selected)}
            title={t('notifications.all.tabtitle')}
            data={getFilteredData()}
            columns={columns}
            options={{ ...tableOptions, ...translatedTextLabels }}
          />
        </ThemeProvider>}
      <Typography color='textDisabled' fontSize='small'>
        {t('notifications.all.dataWarning')}
      </Typography>
    </Stack>
  );
}
